<template>
  <el-dialog
    v-model="dialogVisible"
    :title="title || '编辑帖子'"
    width="600px"
    :close-on-click-modal="false"
    @close="closeDialog"
  >
    <el-form label-width="80px">
      <el-form-item label="帖子标题">
        <el-input
          v-model="editForm.title"
          type="textarea"
          :rows="1"
          placeholder="请输入帖子标题"
          maxlength="100"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="帖子内容">
        <div class="content-with-ai-btn">
          <el-input
            v-model="editForm.content"
            type="textarea"
            :rows="6"
            placeholder="请输入帖子内容"
            maxlength="10000"
            show-word-limit
          />
          <div class="ai-btn" @click="showPolishInstructionDialog = true">AI</div>
        </div>
      </el-form-item>

      <el-form-item label="封面图片">
        <el-upload
          class="cover-uploader"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleCoverChange"
          list-type="picture-card"
        >
          <img v-if="editForm.coverImage" :src="editForm.coverImage" class="cover-image" />
          <el-icon v-else class="cover-uploader-icon">
            <Plus />
          </el-icon>
        </el-upload>
        <!-- 删除图片按钮 -->
        <div v-if="editForm.coverImage" class="delete-cover-btn" @click="removeCoverImage">
          X
        </div>
      </el-form-item>
      
      <!-- 九宫格图片 -->
      <el-form-item label="九宫格图片">
        <div 
          class="media-grid"
          @dragover.prevent
          @dragenter.prevent
        >
          <div 
            v-for="(media, index) in imageMediaList" 
            :key="index" 
            class="media-item image-item"
            :data-index="index"
            draggable="true"
            @dragstart="onImageDragStart($event, index)"
            @dragend="onImageDragEnd"
            @dragover="onImageDragOver"
            @dragenter="onImageDragEnter"
            @dragleave="onImageDragLeave"
            @drop="onImageDrop"
          >
            <div class="media-thumb-container">
              <img :src="media.url" class="media-thumb" />
              <div class="media-actions-overlay">
                <el-icon 
                  class="action-icon delete-icon" 
                  @click.stop="removeMediaByType('image', index)"
                >
                  <Close />
                </el-icon>
              </div>
            </div>
          </div>
          
          <!-- 添加按钮，仅当未达到最大数量时显示 -->
          <div 
            v-if="imageMediaList.length < 9"
            class="media-item empty-grid"
            @click="triggerAddImage"
          >
            <div class="add-image-placeholder">
              <el-icon class="add-icon"><Plus /></el-icon>
              <span class="add-text">添加图片</span>
            </div>
          </div>
        </div>
        
        <!-- 图片上传进度条 -->
        <div v-if="imageUploadProgress.length > 0" class="upload-progress-container">
          <div v-for="(progressItem, index) in imageUploadProgress" :key="'img-progress-' + progressItem.id" class="upload-progress-item">
            <div class="progress-label">图片{{ index + 1 }}上传中... {{ progressItem.progress }}%</div>
            <el-progress :percentage="progressItem.progress" :show-text="false" />
          </div>
        </div>
        
        <div class="el-upload__tip">上传九宫格图片，最多9张，支持拖拽排序</div>
      </el-form-item>
      
      <!-- 视频信息 -->
      <el-form-item label="上传视频">
        <div v-if="videoMediaList.length > 0" class="video-preview-container">
          <div class="video-item">
            <video :src="videoMediaList[0].url" controls class="video-element"></video>
            <div class="media-actions">
              <el-button type="danger" size="small" @click="removeMediaByType('video', 0)">删除视频</el-button>
            </div>
          </div>
        </div>
        <el-upload
          v-else
          class="video-uploader"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleVideoChange"
          accept="video/*"
          :limit="1"
        >
          <el-button size="small" type="primary">上传视频</el-button>
        </el-upload>
        
        <!-- 视频上传进度条 -->
        <div v-if="videoUploadProgress > 0" class="upload-progress-container">
          <div class="upload-progress-item">
            <div class="progress-label">视频上传中... {{ videoUploadProgress }}%</div>
            <el-progress :percentage="videoUploadProgress" :show-text="false" />
          </div>
        </div>
        
        <div class="el-upload__tip">只能上传一个视频文件</div>
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="submitEdit" :loading="submitting">确定</el-button>
      </span>
    </template>
    
    <!-- AI润色指令输入对话框 -->
    <el-dialog
      v-model="showPolishInstructionDialog"
      title="AI润色指令"
      width="400px"
      center
      class="ai-polish-instruction-dialog"
    >
      <el-input
        v-model="polishInstruction"
        type="textarea"
        :rows="4"
        placeholder="请输入润色指令，例如：让语言更生动有趣、调整为教程风格等"
        maxlength="200"
        show-word-limit
        class="custom-textarea"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showPolishInstructionDialog = false" class="cancel-btn">取消</el-button>
          <el-button 
            type="primary" 
            @click="startPolishContent" 
            :loading="isPolishing"
            class="confirm-btn"
          >
            {{ isPolishing ? '润色中...' : '开始润色' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- AI润色结果对比对话框 -->
    <el-dialog
      v-model="showPolishResultDialog"
      title="AI润色结果对比"
      width="80%"
      top="5vh"
      class="polish-result-dialog"
    >
      <div class="polish-comparison-container">
        <div class="comparison-panel">
          <h3>原始内容</h3>
          <div class="content-panel original-content">
            <pre>{{ editForm.content }}</pre>
          </div>
        </div>
        <div class="comparison-panel">
          <h3>AI润色内容</h3>
          <div class="content-panel polished-content">
            <div v-if="isPolishing" class="loading-indicator">
              <el-icon :class="['is-loading']"><Loading /></el-icon>
              <span>AI正在润色中...</span>
            </div>
            <div class="markdown-content" v-html="renderedPolishedContent"></div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer comparison-footer">
          <el-button @click="showPolishResultDialog = false" class="cancel-btn">不接受</el-button>
          <el-button 
            type="primary" 
            @click="acceptPolishedContent" 
            :disabled="isPolishing || !polishedContent"
            class="confirm-btn"
          >
            接受润色结果
          </el-button>
        </div>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Close, Loading } from '@element-plus/icons-vue';
import { getPostDetail, editPost, uploadImages, uploadVideo, uploadCoverImage } from '@/user/api/post';
import MarkdownIt from 'markdown-it';

export default defineComponent({
  name: 'EditPostModal',
  components: {
    Plus,
    Close,
    Loading
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    postId: {
      type: Number,
      default: null
    },
    title: {
      type: String,
      default: '编辑帖子'
    }
  },
  emits: ['update:modelValue', 'save-success', 'close'],
  setup(props, { emit }) {
    // 响应式数据
    const dialogVisible = computed({
      get() {
        return props.modelValue;
      },
      set(value) {
        emit('update:modelValue', value);
      }
    });

    const editForm = ref({
      title: '',
      content: '',
      coverImage: '',
      mediaList: [] as any[]
    });

    const editingPost = ref<any>(null);
    const imageUploadProgress = ref<{id: number, progress: number}[]>([]);
    const videoUploadProgress = ref<number>(0);
    const submitting = ref(false);

    // AI润色相关状态
    const showPolishInstructionDialog = ref<boolean>(false);
    const showPolishResultDialog = ref<boolean>(false);
    const polishInstruction = ref<string>('让语言更生动有趣');
    const isPolishing = ref<boolean>(false);
    const polishedContent = ref<string>('');
    const md = new MarkdownIt();

    // 渲染后的Markdown内容
    const renderedPolishedContent = computed(() => {
      return polishedContent.value ? md.render(polishedContent.value) : '';
    });

    // 计算属性：分离图片和视频
    const imageMediaList = computed(() => {
      const images = (editForm.value.mediaList || [])
        .filter(m => m.mediaType === 1)
        .map(m => {
          const imageUrl = m.url || m.mediaUrl || m.originalUrl;
          return {
            ...m,
            url: processMediaUrl(imageUrl)
          };
        });

      return images;
    });

    const videoMediaList = computed(() => {
      const videos = (editForm.value.mediaList || [])
        .filter(m => m.mediaType === 2)
        .map(m => {
          const videoUrl = m.url || m.mediaUrl || m.originalUrl;
          return {
            ...m,
            url: processMediaUrl(videoUrl)
          };
        });

      return videos;
    });

    // 开始AI润色
    const startPolishContent = async () => {
      if (!editForm.value.content.trim()) {
        ElMessage.warning('请先输入帖子内容');
        return;
      }

      showPolishInstructionDialog.value = false;
      showPolishResultDialog.value = true;
      isPolishing.value = true;
      polishedContent.value = '';

      try {
        const token = localStorage.getItem('token'); // 获取token

        const apiResponse = await fetch('/api/user/post/polish', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'token': token || '' // 根据项目规范，使用token字段而不是Authorization: Bearer
          },
          body: JSON.stringify({
            content: editForm.value.content,           // 需要润色的原始内容
            instruction: polishInstruction.value || '让语言更生动有趣'  // 润色指令
          })
        });

        if (!apiResponse.ok) {
          throw new Error(`润色请求失败: ${apiResponse.status} ${apiResponse.statusText}`);
        }

        // 检查apiResponse.body是否为null
        if (!apiResponse.body) {
          throw new Error('响应体为空，无法读取');
        }

        const reader = apiResponse.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = ''; // 用于处理可能分割的数据块


        let shouldContinue = true;
        while (shouldContinue) {
          const { done, value } = await reader.read();


          if (done) {

            shouldContinue = false;
            break;
          }

          // 解码当前数据块
          const chunk = decoder.decode(value, { stream: true });


          buffer += chunk;

          // 按行分割缓冲区
          const lines = buffer.split('\n');


          // 保留最后一个不完整的行
          buffer = lines.pop() || '';


          // 遍历所有行进行处理
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];


            // 检查是否以 'data:' 开头（兼容带空格和不带空格的格式）
            if (line.startsWith('data:')) {


              // 提取冒号后的部分，兼容 'data:content' 和 'data: content' 两种格式
              let data = line.substring(5); // 移除 'data:' 部分
              data = data.trimStart(); // 去除开头可能的空格


              // 检查是否是结束标记（如果有的话）
              if (data === '[DONE]' || data === '') {

                continue;
              }

              // 将数据追加到润色内容中
              const previousValue = polishedContent.value;

              // 检查当前内容是否以换行符结尾，如果不是且新数据非空，则添加换行符
              if (polishedContent.value.length > 0 && !polishedContent.value.endsWith('\n') && data) {
                polishedContent.value += '\n';
              }

              polishedContent.value += data;


              // 强制触发Vue响应式更新
              await nextTick();
            } else if (line.trim() === '') {
              // 忽略空行
              console.log('检测到空行，跳过');
            } else {
              console.log('非数据行，跳过:', JSON.stringify(line));
            }
          }
        }

        // 处理缓冲区中剩余的数据（防止末尾数据丢失）
        if (buffer) {
          console.log('处理缓冲区中剩余数据:', JSON.stringify(buffer));
          if (buffer.startsWith('data:')) {
            let data = buffer.substring(5); // 移除 'data:' 部分
            data = data.trimStart(); // 去除开头可能的空格

            if (data !== '[DONE]' && data !== '') {
              console.log('处理缓冲区最后的数据:', JSON.stringify(data));
              if (polishedContent.value.length > 0 && !polishedContent.value.endsWith('\n') && data) {
                polishedContent.value += '\n';
              }
              polishedContent.value += data;
            }
          }
        }


        // 如果最终值为空，输出警告
        if (!polishedContent.value) {
          console.warn('polishedContent最终值为空，可能处理过程中出现问题');
        }
      } catch (error: any) {
        console.error('润色内容失败:', error);
        ElMessage.error('润色内容失败: ' + (error.message || '未知错误'));
      } finally {
        isPolishing.value = false;

      }
    };

    // 接受润色后的内容
    const acceptPolishedContent = () => {
      if (polishedContent.value) {
        editForm.value.content = polishedContent.value;
        ElMessage.success('已接受润色后的内容！');
        showPolishResultDialog.value = false;
      }
    };

    // 加载帖子详情
    const loadPostDetails = async (postId: number) => {
      try {
        const response = await getPostDetail(postId);
        
        if (response.code === 1 && response.data) {
          editingPost.value = response.data;
          
          editForm.value = {
            title: response.data.title,
            content: response.data.content,
            coverImage: response.data.coverImage || '',
            mediaList: response.data.mediaList || []
          };
        } else {
          ElMessage.error(response.msg || '获取帖子详情失败');
        }
      } catch (error) {
        console.error('获取帖子详情失败:', error);
        ElMessage.error('获取帖子详情失败');
      }
    };

    // 重置表单
    const resetForm = () => {
      editForm.value = {
        title: '',
        content: '',
        coverImage: '',
        mediaList: []
      };
      editingPost.value = null;
      imageUploadProgress.value = [];
      videoUploadProgress.value = 0;
    };

    // 监听postId变化，加载帖子详情
    watch(() => props.postId, async (newPostId) => {
      if (newPostId) {
        await loadPostDetails(newPostId);
      } else {
        // 当postId变为null时，重置表单
        resetForm();
      }
    }, { immediate: true });

    // 处理封面图片变化
    const handleCoverChange = async (uploadFile: any) => {
      const file = uploadFile.raw;
      if (!file) {
        console.error('未找到原始文件');
        return;
      }

      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
      const isLt5M = file.size / 1024 / 1024 < 5; // 限制为5MB

      if (!isJPG) {
        ElMessage.error('上传封面图片只能是 JPG/PNG/GIF 格式!');
        return;
      }
      if (!isLt5M) {
        ElMessage.error('上传封面图片大小不能超过 5MB!');
        return;
      }

      try {
        // 调用API上传封面图片
        const response = await uploadCoverImage(file);
        
        if (response && response.code === 1) {
          let coverImageUrl = response.data;
          
          editForm.value.coverImage = coverImageUrl;
          ElMessage.success('封面图片上传成功!');
        } else {
          const errorMsg = response?.msg || '封面图片上传失败';
          ElMessage.error(errorMsg);
        }
      } catch (error: any) {
        console.error('上传失败:', error);
        // 检查是否是413错误
        if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 413) {
          ElMessage.error('封面图片文件过大，请压缩后再上传');
        } else {
          let errorMessage = '封面图片上传失败';
          
          if (error.response && error.response.data && error.response.data.msg) {
            errorMessage = error.response.data.msg;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          ElMessage.error(errorMessage);
        }
      }
    };

    // 删除封面图片
    const removeCoverImage = () => {
      editForm.value.coverImage = '';
      ElMessage.info('封面图片已删除');
    };

    // 处理图片文件上传
    const handleImageChange = async (uploadFile: any) => {
      const file = uploadFile.raw;
      if (!file) {
        console.error('未找到原始文件');
        return;
      }

      const isImage = file.type.startsWith('image/');
      
      if (!isImage) {
        ElMessage.error('仅支持上传图片文件!');
        return;
      }

      const isLt20M = file.size / 1024 / 1024 < 20;

      if (!isLt20M) {
        ElMessage.error('上传文件大小不能超过 20MB!');
        return;
      }

      const currentImageCount = (editForm.value.mediaList || []).filter(m => m.mediaType === 1).length;
      if (currentImageCount >= 9) {
        ElMessage.error('九宫格图片最多只能上传9张!');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('files', file);
        
        const uploadId = Date.now() + Math.random();
        const initialProgressIndex = imageUploadProgress.value.length;
        
        imageUploadProgress.value = [
          ...imageUploadProgress.value, 
          { id: uploadId, progress: 0 }
        ];

        const response = await uploadImages(formData, (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (initialProgressIndex < imageUploadProgress.value.length) {
            imageUploadProgress.value.splice(initialProgressIndex, 1, { 
              id: uploadId, 
              progress: progress 
            });
            imageUploadProgress.value = [...imageUploadProgress.value];
          }
        });

        if (response.code === 1 && response.data && response.data.length > 0) {
          const uploadedUrl = response.data[0];
          const mediaObj: any = {
            mediaUrl: uploadedUrl,
            mediaType: 1, // 图片
            width: 0,
            height: 0,
            sortOrder: editForm.value.mediaList.length,
            thumbnailUrl: null,
            duration: 0,
            url: processMediaUrl(uploadedUrl)
          };

          try {
            const img = new Image();
            img.src = processMediaUrl(uploadedUrl);
            await new Promise<void>((resolve) => {
              img.onload = () => {
                mediaObj.width = img.width;
                mediaObj.height = img.height;
                resolve();
              };
              img.onerror = () => resolve();
            });
          } catch (e) {
            console.error("获取图片尺寸失败:", e);
          }

          editForm.value.mediaList.push(mediaObj);
          resortMediaList();
          
          setTimeout(() => {
            imageUploadProgress.value = imageUploadProgress.value.filter(item => item.id !== uploadId);
          }, 1000);
        } else {
          ElMessage.error(response.msg || '上传图片失败');
          setTimeout(() => {
            imageUploadProgress.value = imageUploadProgress.value.filter(item => item.id !== uploadId);
          }, 1000);
        }
      } catch (error: any) {
        console.error('上传图片失败:', error);
        ElMessage.error('上传图片失败: ' + (error.message || '未知错误'));
        setTimeout(() => {
          imageUploadProgress.value.pop();
        }, 1000);
      }
    };

    // 批量上传图片文件
    const handleBatchImageUpload = async (files: File[]) => {
      // 验证文件类型和大小
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          ElMessage.error('仅支持上传图片文件!');
          return;
        }

        const isLt20M = file.size / 1024 / 1024 < 20;
        if (!isLt20M) {
          ElMessage.error('上传文件大小不能超过 20MB!');
          return;
        }
      }

      const currentImageCount = (editForm.value.mediaList || []).filter(m => m.mediaType === 1).length;
      if (currentImageCount + files.length > 9) {
        ElMessage.error(`九宫格图片最多只能上传9张! 您还可以上传 ${9 - currentImageCount} 张图片`);
        return;
      }

      // 为每个文件创建上传进度条的ID
      const uploadIds = files.map(() => Date.now() + Math.random());
      
      try {
        const formData = new FormData();
        files.forEach(file => {
          formData.append('files', file);
        });
        
        const initialProgressIndex = imageUploadProgress.value.length;
        
        // 初始化所有上传进度为0
        files.forEach((_, index) => {
          imageUploadProgress.value.push({ id: uploadIds[index], progress: 0 });
        });

        // 批量上传图片
        const response = await uploadImages(formData, (progressEvent: any) => {
          // 计算总体进度
          const overallProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // 平均分配给每个文件
          const individualProgress = Math.floor(overallProgress / files.length);
          
          uploadIds.forEach((id, index) => {
            const progressIndex = initialProgressIndex + index;
            if (progressIndex < imageUploadProgress.value.length) {
              imageUploadProgress.value.splice(progressIndex, 1, { 
                id: id, 
                progress: individualProgress 
              });
            }
          });
          imageUploadProgress.value = [...imageUploadProgress.value];
        });

        if (response.code === 1 && response.data && response.data.length > 0) {
          // 为每张上传成功的图片创建媒体对象
          for (let i = 0; i < response.data.length; i++) {
            const uploadedUrl = response.data[i];
            const mediaObj: any = {
              mediaUrl: uploadedUrl,
              mediaType: 1, // 图片
              width: 0,
              height: 0,
              sortOrder: editForm.value.mediaList.length,
              thumbnailUrl: null,
              duration: 0,
              url: processMediaUrl(uploadedUrl)
            };

            try {
              const img = new Image();
              img.src = processMediaUrl(uploadedUrl);
              await new Promise<void>((resolve) => {
                img.onload = () => {
                  mediaObj.width = img.width;
                  mediaObj.height = img.height;
                  resolve();
                };
                img.onerror = () => resolve();
              });
            } catch (e) {
              console.error("获取图片尺寸失败:", e);
            }

            editForm.value.mediaList.push(mediaObj);
          }
          
          resortMediaList();
          
          // 移除所有相关的进度条
          setTimeout(() => {
            imageUploadProgress.value = imageUploadProgress.value.filter(item => 
              !uploadIds.includes(item.id)
            );
          }, 1000);
        } else {
          ElMessage.error(response.msg || '上传图片失败');
          setTimeout(() => {
            imageUploadProgress.value = imageUploadProgress.value.filter(item => 
              !uploadIds.includes(item.id)
            );
          }, 1000);
        }
      } catch (error: any) {
        console.error('批量上传图片失败:', error);
        ElMessage.error('上传图片失败: ' + (error.message || '未知错误'));
        setTimeout(() => {
          // 清除本次上传的进度条
          imageUploadProgress.value = imageUploadProgress.value.filter(item => 
            !uploadIds.includes(item.id)
          );
        }, 1000);
      }
    };

    // 处理视频文件上传
    const handleVideoChange = async (uploadFile: any) => {
      const file = uploadFile.raw;
      if (!file) {
        console.error('未找到原始文件');
        return;
      }

      const isVideo = file.type.startsWith('video/');
      
      if (!isVideo) {
        ElMessage.error('仅支持上传视频文件!');
        return;
      }

      const isLt20M = file.size / 1024 / 1024 < 20;

      if (!isLt20M) {
        ElMessage.error('上传文件大小不能超过 20MB!');
        return;
      }

      const currentVideoCount = (editForm.value.mediaList || []).filter(m => m.mediaType === 2).length;
      if (currentVideoCount >= 1) {
        ElMessage.error('只能上传一个视频文件!');
        return;
      }

      try {
        // 注意：这里需要传入File对象而不是FormData，与API函数签名匹配
        const updateProgress = (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          videoUploadProgress.value = progress;
        };

        const response = await uploadVideo(file, updateProgress);

        if (response.code === 1 && response.data) {
          const uploadedUrl = response.data;
          const mediaObj: any = {
            mediaUrl: uploadedUrl,
            mediaType: 2, // 视频
            width: 0,
            height: 0,
            sortOrder: editForm.value.mediaList.length,
            thumbnailUrl: null,
            duration: 0,
            url: processMediaUrl(uploadedUrl)
          };

          editForm.value.mediaList.push(mediaObj);
          resortMediaList();
          videoUploadProgress.value = 0;
        } else {
          ElMessage.error(response.msg || '上传视频失败');
        }
      } catch (error: any) {
        console.error('上传视频失败:', error);
        ElMessage.error('上传视频失败: ' + (error.message || '未知错误'));
      }
    };

    // 重新排序媒体列表
    const resortMediaList = () => {
      const images = editForm.value.mediaList
        .filter(item => item.mediaType === 1)
        .map(item => ({...item}));
      const videos = editForm.value.mediaList
        .filter(item => item.mediaType === 2)
        .map(item => ({...item}));
      
      const reorderedMediaList = [...images, ...videos];
      
      reorderedMediaList.forEach((item, idx) => {
        item.sortOrder = idx;
      });
      
      editForm.value.mediaList = reorderedMediaList;
    };

    // 移除媒体项
    const removeMediaByType = (type: 'image' | 'video', index: number) => {
      let count = 0;
      for (let i = 0; i < editForm.value.mediaList.length; i++) {
        if (editForm.value.mediaList[i].mediaType === (type === 'image' ? 1 : 2)) {
          if (count === index) {
            editForm.value.mediaList.splice(i, 1);
            break;
          }
          count++;
        }
      }
      resortMediaList();
    };

    // 拖拽相关功能
    let draggedItemIndex = 0;

    const onImageDragStart = (event: DragEvent, index: number) => {
      draggedItemIndex = index;
      event.dataTransfer?.setData('text/plain', index.toString());
    };

    const onImageDragEnd = () => {
      draggedItemIndex = 0;
    };

    const onImageDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    const onImageDragEnter = (event: DragEvent) => {
      event.preventDefault();
      // 确保currentTarget不为null，并且是HTMLElement
      if (event.currentTarget && event.currentTarget instanceof HTMLElement) {
        event.currentTarget.classList.add('over');
      }
    };

    const onImageDragLeave = (event: DragEvent) => {
      // 确保currentTarget不为null，并且是HTMLElement
      if (event.currentTarget && event.currentTarget instanceof HTMLElement) {
        event.currentTarget.classList.remove('over');
      }
    };

    const onImageDrop = (event: DragEvent) => {
      event.preventDefault();
      
      // 查找最近的具有data-index属性的父元素
      let targetElement: HTMLElement | null = event.target as HTMLElement;
      while (targetElement && !targetElement.hasAttribute('data-index')) {
        targetElement = targetElement.parentElement;
      }
      
      if (!targetElement) {
        console.error('找不到带有data-index属性的元素');
        return;
      }
      
      const dropIndex = parseInt(targetElement.getAttribute('data-index') || '');
      
      if (isNaN(dropIndex)) {
        console.error('无效的drop索引');
        return;
      }

      if (!isNaN(draggedItemIndex) && draggedItemIndex !== dropIndex) {
        const images = editForm.value.mediaList.filter(m => m.mediaType === 1);
        
        // 检查索引是否有效
        if (draggedItemIndex >= images.length || dropIndex >= images.length) {
          console.error('拖拽索引超出范围');
          return;
        }
        
        const draggedMedia = images[draggedItemIndex];
        const droppedMedia = images[dropIndex];
        
        const draggedOriginalIndex = editForm.value.mediaList.indexOf(draggedMedia);
        const droppedOriginalIndex = editForm.value.mediaList.indexOf(droppedMedia);
        
        if (draggedOriginalIndex !== -1 && droppedOriginalIndex !== -1) {
          const temp = editForm.value.mediaList[draggedOriginalIndex];
          editForm.value.mediaList[draggedOriginalIndex] = editForm.value.mediaList[droppedOriginalIndex];
          editForm.value.mediaList[droppedOriginalIndex] = temp;
          
          resortMediaList();
        }
      }
      
      // 移除所有元素的'over'类
      const overElements = document.querySelectorAll('.media-item.over');
      overElements.forEach(el => el.classList.remove('over'));
      
      draggedItemIndex = 0;
    };

    const triggerAddImage = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.onchange = (e: any) => {
        const files = Array.from(e.target.files) as File[];
        if (files.length > 0) {
          handleBatchImageUpload(files);
        }
      };
      input.click();
    };

    // 处理媒体URL
    const processMediaUrl = (url: string): string => {
      if (!url) {
        return '';
      }
      
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      const baseURL = import.meta.env.VITE_API_BASE_URL || '';
      
      if (url.startsWith('/uploads')) {
        if (baseURL) {
          return `${baseURL}${url}`;
        }
        if (!import.meta.env.PROD) {
          return `/api${url}`;
        }
        return `/api${url}`;
      }
      
      return url;
    };

    // 提交编辑
    const submitEdit = async () => {
      if (!editingPost.value) {
        ElMessage.error('没有选中要编辑的帖子');
        return;
      }

      submitting.value = true;

      try {
        ElMessage.info('正在处理媒体文件...');
        
        const localFiles = editForm.value.mediaList.filter(m => m.file !== undefined);
        const uploadedFiles = editForm.value.mediaList.filter(m => m.file === undefined);

        let finalMediaList: any[] = [...uploadedFiles];

        if (localFiles.length > 0) {
          const localImages = localFiles.filter(m => m.mediaType === 1);
          const localVideos = localFiles.filter(m => m.mediaType === 2);

          if (localImages.length > 0) {
            const imageFiles = localImages.map(m => m.file);
            const imageUrls = await uploadImagesWithProgress(imageFiles);
            
            imageUrls.forEach((url: string, index: number) => {
              finalMediaList.push({
                mediaUrl: url,
                mediaType: 1,
                width: localImages[index].width,
                height: localImages[index].height,
                sortOrder: localImages[index].sortOrder,
                thumbnailUrl: localImages[index].thumbnailUrl,
                duration: localImages[index].duration
              });
            });
          }

          if (localVideos.length > 0) {
            const videoFile = localVideos[0].file;
            const videoUrl = await uploadVideoWithProgress(videoFile);
            
            finalMediaList.push({
              mediaUrl: videoUrl,
              mediaType: 2,
              width: localVideos[0].width,
              height: localVideos[0].height,
              sortOrder: localVideos[0].sortOrder,
              thumbnailUrl: localVideos[0].thumbnailUrl,
              duration: localVideos[0].duration
            });
          }
        } else {
          finalMediaList = editForm.value.mediaList.map(m => {
            const { file, ...rest } = m;
            return rest;
          });
        }

        finalMediaList.forEach((item, index) => {
          item.sortOrder = index;
        });

        const params = {
          id: editingPost.value.id,
          title: editForm.value.title,
          content: editForm.value.content,
          coverImage: editForm.value.coverImage,
          mediaList: finalMediaList
        };

        const response = await editPost(params);
        
        if (response.code === 1) {
          ElMessage.success(response.msg || '编辑帖子成功');
          dialogVisible.value = false;
          imageUploadProgress.value = [];
          videoUploadProgress.value = 0;
          emit('save-success');
        } else {
          ElMessage.error(response.msg || '编辑帖子失败');
        }
      } catch (error: any) {
        console.error('编辑帖子失败:', error);
        ElMessage.error(error.message || '编辑帖子失败');
      } finally {
        submitting.value = false;
      }
    };

    // 上传进度处理函数
    const uploadImagesWithProgress = async (files: File[]): Promise<string[]> => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const response = await uploadImages(formData, () => {/* 空函数 */});
      return response.data || [];
    };

    const uploadVideoWithProgress = async (file: File): Promise<string> => {
      const response = await uploadVideo(file, () => {/* 空函数 */});
      return response.data;
    };

    // 关闭对话框
    const closeDialog = () => {
      dialogVisible.value = false;
      resetForm();
      emit('close'); // 发出关闭事件，让父组件重置postId
    };

    return {
      dialogVisible,
      editForm,
      editingPost,
      imageUploadProgress,
      videoUploadProgress,
      submitting,
      imageMediaList,
      videoMediaList,
      loadPostDetails,
      handleCoverChange,
      removeCoverImage,
      handleImageChange,
      handleVideoChange,
      resortMediaList,
      removeMediaByType,
      onImageDragStart,
      onImageDragEnd,
      onImageDragOver,
      onImageDragEnter,
      onImageDragLeave,
      onImageDrop,
      triggerAddImage,
      processMediaUrl,
      submitEdit,
      closeDialog,
      resetForm,
      // AI润色相关
      showPolishInstructionDialog,
      showPolishResultDialog,
      polishInstruction,
      isPolishing,
      polishedContent,
      renderedPolishedContent,
      startPolishContent,
      acceptPolishedContent
    };
  }
});
</script>

<style scoped>
.cover-uploader {
  width: 100%;
  max-width: 100%;
}

/* 使用padding-top百分比实现16:9比例 */
.cover-uploader .el-upload {
  width: 100% !important;
  position: relative;
  height: 0 !important;
  padding-top: 56.25% !important; /* 16:9 比例 = 9/16 = 0.5625 = 56.25% */
  border: 1px dashed var(--el-border-color);
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
}

.cover-uploader .el-upload:hover {
  border-color: var(--el-color-primary);
}

.cover-uploader .cover-image {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 8px;
}

.cover-uploader-icon {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #8c939d;
  text-align: center;
}

.delete-cover-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background: rgba(245, 108, 108, 0.85); /* 更明显的红色 */
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 12px;
  line-height: 1;
  transition: opacity 0.3s;
  border: 1px solid white; /* 添加白色边框以提高对比度 */
}

.cover-uploader:hover .delete-cover-btn {
  opacity: 1;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 15px;
  width: 100%;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
}

.media-item {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  aspect-ratio: 1/1;
  background: #fff;
  cursor: move;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.media-item:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.media-item.image-item.dragging {
  opacity: 0.6;
  border: 2px dashed #409eff;
  transform: scale(0.95);
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.3);
}

.media-item.image-item.over {
  border: 2px solid #409eff;
  background-color: #f0f9ff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.media-thumb-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.media-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-actions-overlay {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 10;
}

.media-item:hover .media-actions-overlay {
  opacity: 1;
}

.action-icon {
  color: white;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s;
}

.action-icon.delete-icon {
  background: transparent;
}

.action-icon.delete-icon:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #f56c6c;
  transform: scale(1.1);
}

.empty-grid {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #c0c4cc;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.3s;
  aspect-ratio: 1/1;
}

.empty-grid:hover {
  border-color: #409eff;
  background-color: #f5fbff;
  transform: translateY(-2px);
}

.add-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #c0c4cc;
}

.add-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.add-text {
  font-size: 12px;
}

.video-preview-container {
  margin-bottom: 15px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.video-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.video-element {
  width: 100%;
  max-width: 200px;
  height: auto;
  border-radius: 4px;
  margin-right: 15px;
  border: 1px solid #eee;
}

.media-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-progress-container {
  margin-top: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.upload-progress-item {
  margin-bottom: 12px;
}

.progress-label {
  margin-bottom: 6px;
  font-size: 13px;
  color: #606266;
  display: flex;
  justify-content: space-between;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.el-form-item {
  margin-bottom: 20px;
}

.el-form-item__label {
  font-weight: 600;
  color: #303133;
}

.el-upload__tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  text-align: center;
}

/* AI润色功能样式 */
.content-with-ai-btn {
  position: relative;
  display: inline-flex;
  width: 100%;
}

.content-with-ai-btn :deep(.el-textarea__inner) {
  padding-right: 50px; /* 为AI按钮留出空间 */
}

.ai-btn {
  position: absolute;
  right: 12px;
  bottom: 12px;
  background: #409eff;
  color: white;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  z-index: 10;
  user-select: none;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.ai-btn:hover {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
}

/* AI润色指令对话框样式 */
.ai-polish-instruction-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.custom-textarea :deep(.el-textarea__inner) {
  min-height: 100px;
}

/* AI润色结果对比对话框样式 */
.polish-result-dialog :deep(.el-dialog__body) {
  padding: 15px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.polish-comparison-container {
  display: flex;
  gap: 20px;
  height: 500px;
}

.comparison-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 防止内容溢出 */
}

.comparison-panel h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.content-panel {
  flex: 1;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 15px;
  overflow-y: auto;
  background-color: #fafafa;
}

.original-content pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  line-height: 1.6;
  font-family: inherit;
  font-size: 14px;
  color: #606266;
}

.polished-content {
  position: relative;
  background-color: #fff;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #409eff;
}

.loading-indicator .is-loading {
  font-size: 32px;
  margin-bottom: 10px;
  animation: rotating 2s linear infinite;
}

.loading-indicator span {
  font-size: 14px;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Markdown 渲染内容样式 */
.markdown-content {
  line-height: 1.6;
  color: #303133;
  font-size: 14px;
}

.markdown-content p {
  margin: 0 0 10px 0;
}

.markdown-content p:last-child {
  margin-bottom: 0;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin: 15px 0 10px 0;
  font-weight: 600;
  line-height: 1.25;
  color: #303133;
}

.markdown-content h1 { font-size: 1.5em; }
.markdown-content h2 { font-size: 1.3em; }
.markdown-content h3 { font-size: 1.1em; }

.markdown-content ul,
.markdown-content ol {
  padding-left: 20px;
  margin: 10px 0;
}

.markdown-content li {
  margin: 5px 0;
}

.markdown-content img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 10px 0;
}

.markdown-content blockquote {
  border-left: 4px solid #409eff;
  padding: 10px 15px;
  margin: 10px 0;
  color: #606266;
  background-color: #f5f7fa;
  border-radius: 0 4px 4px 0;
}

.markdown-content code {
  background-color: #f2f4f7;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
  color: #e6a23c;
}

.markdown-content pre {
  background-color: #282c34;
  color: #abb2bf;
  padding: 15px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 10px 0;
}

.markdown-content pre code {
  background-color: transparent;
  color: inherit;
  padding: 0;
}

.comparison-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .polish-comparison-container {
    flex-direction: column;
    height: auto;
  }
  
  .comparison-panel {
    height: 300px;
  }
}
</style>